// The toast surface (prototype .toast — the dark Forest-deep slip, bottom-centre; design brief §4: toasts
// stand in for out-of-prototype actions and their copy states the intended behaviour). A single transient
// message, auto-dismissed. It is a true elevation surface, so it carries a shadow (design brief §3: shadows
// only for true elevation — menus/dialogs/toasts). Announced politely for assistive tech (role=status).
// Tokens only (G-181).

import { useEffect, useState } from 'react';
import { useShell } from './ShellContext.tsx';

/** How long a toast stays before auto-dismiss (matches the prototype's 2.6s). */
const TOAST_MS = 2600;

/** Render the current toast (nothing when there is none); auto-clears after TOAST_MS. */
export function Toast(): React.JSX.Element | null {
  const shell = useShell();
  const message = shell.toast;
  const clearToast = shell.clearToast;

  // The prototype's entrance: mount, then add `on` next frame so the rise transition runs.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (message === null) {
      setEntered(false);
      return;
    }
    const frame = requestAnimationFrame(() => setEntered(true));
    const timer = window.setTimeout(clearToast, TOAST_MS);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [message, clearToast]);

  if (message === null) return null;
  return (
    <div role="status" aria-live="polite" className={entered ? 'toast on' : 'toast'}>
      {message}
    </div>
  );
}
